import type { CanonicalAdEvent, VendorInitiatingTarget } from './schemas';
import { APP_NAME, APP_VERSION, EVENT_NAMES, WORKFLOW_IDS } from './constants';
import type { InvokeRequest } from './schemas';
import type { AdProperties, AnalyticsConfig } from './types';
import { willSkipAnalytics } from '../../willSkipAnalytics';

type EventName = (typeof EVENT_NAMES)[keyof typeof EVENT_NAMES];

const sessionFiredEvents = new Map<string, Set<string>>();

function getSessionFiredEvents(sessionId: string): Set<string> {
  let events = sessionFiredEvents.get(sessionId);
  if (!events) {
    events = new Set();
    sessionFiredEvents.set(sessionId, events);
  }
  return events;
}

export class Analytics {
  private config: AnalyticsConfig;
  private firedEvents: Set<string>;
  private pageStartTime: number;
  private widgetLoadTime: number | null;

  constructor(config: AnalyticsConfig) {
    this.config = config;
    this.firedEvents = getSessionFiredEvents(config.sessionId);
    this.pageStartTime = performance.now();
    this.widgetLoadTime = null;
  }

  private generateEventKey(eventName: EventName, offerId?: string): string {
    return offerId ? `${eventName}:${offerId}` : eventName;
  }

  private shouldFireEvent(eventName: EventName, offerId?: string): boolean {
    const eventKey = this.generateEventKey(eventName, offerId);
    return !this.firedEvents.has(eventKey);
  }

  private markEventFired(eventName: EventName, offerId?: string): void {
    const eventKey = this.generateEventKey(eventName, offerId);
    this.firedEvents.add(eventKey);
  }

  private getEventOffsetMs(): number {
    return Math.round(performance.now() - this.pageStartTime);
  }

  private buildAdEventFields(ad: AdProperties) {
    const { offerContent } = ad;
    return {
      vendorOfferId: ad.offerId,
      vendorAdvertiserId: ad.advertiserId,
      vendorAdvertiserName: ad.advertiserName,
      vendorInsertionId: ad.insertionId,
      vendorPosition: ad.position,
      ...(offerContent && {
        offerHeading: offerContent.heading,
        offerSubheading: offerContent.subheading,
        offerTargetUrl: offerContent.targetUrl,
        offerAcceptCopy: offerContent.acceptCopy,
        offerDeclineCopy: offerContent.declineCopy,
        ...(offerContent.featuredImageUrl && {
          offerFeaturedImageUrl: offerContent.featuredImageUrl,
        }),
        offerLayoutType: offerContent.layoutType,
        vendorAdvertiserLogoUrl: offerContent.vendorAdvertiserLogoUrl,
      }),
    };
  }

  private buildPayloadAttributes(extra?: Record<string, unknown>) {
    const attributes = { ...this.config.payloadAttributes, ...extra };
    return Object.keys(attributes).length
      ? { payloadAttributes: attributes }
      : {};
  }

  private buildBaseEvent(): Omit<
    CanonicalAdEvent,
    'eventName' | 'eventOffsetMs'
  > {
    return {
      sessionId: this.config.sessionId,
      vendorSessionId: this.config.vendorSessionId,
      device: this.config.device,
      appName: APP_NAME,
      appVersion: APP_VERSION,
      pageUrl: this.config.pageUrl,
      layout:
        this.config.view === 'THANK_YOU' && this.config.layout !== 'DRAWER'
          ? `TYP_${this.config.layout}`
          : this.config.layout,
      versionName: this.config.versionName,
      locale: this.config.locale,
      browserFingerprint: this.config.browserFingerprint,
      ...(this.config.orderNumber && { orderNumber: this.config.orderNumber }),
      ...(this.config.trackingNumber && {
        trackingNumber: this.config.trackingNumber,
      }),
      ...(this.config.trackingStatus && {
        trackingStatus: this.config.trackingStatus,
      }),
      ...(this.config.externalId && { externalId: this.config.externalId }),
      ...(this.config.decisionId && { decisionId: this.config.decisionId }),
      ...(this.config.isFallback && { isFallback: true }),
      ...(this.config.experiments && {
        experiments: this.config.experiments,
      }),
      ...this.buildPayloadAttributes(),
    };
  }

  private async sendEvent(
    event: CanonicalAdEvent,
    extras?: Record<string, unknown>,
  ): Promise<void> {
    if (willSkipAnalytics(this.config.pageUrl)) {
      return;
    }

    const invokeRequest: InvokeRequest = {
      workflowId: WORKFLOW_IDS.DISCO_EVENTS,
      context: {
        retailerMoniker: this.config.retailerMoniker,
      },
      body: extras ? { ...extras, ...event } : event,
    };

    try {
      await fetch(`${this.config.gatewayUrl}/invoke`, {
        method: 'POST',
        keepalive: true,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${this.config.jwt}`,
        },
        body: JSON.stringify(invokeRequest),
      });
    } catch (error) {
      console.error('Failed to send canonical event:', error);
    }
  }

  async trackWidgetLoad(): Promise<void> {
    if (!this.shouldFireEvent(EVENT_NAMES.WIDGET_LOAD)) return;
    this.markEventFired(EVENT_NAMES.WIDGET_LOAD);

    this.widgetLoadTime = performance.now();

    await this.sendEvent({
      ...this.buildBaseEvent(),
      eventName: EVENT_NAMES.WIDGET_LOAD,
      eventOffsetMs: this.getEventOffsetMs(),
      ...(this.config.numAdsReturned && {
        numAdsReturned: this.config.numAdsReturned,
      }),
    });
  }

  async trackWidgetDisplay(): Promise<void> {
    if (!this.shouldFireEvent(EVENT_NAMES.WIDGET_DISPLAY)) return;
    this.markEventFired(EVENT_NAMES.WIDGET_DISPLAY);

    await this.sendEvent({
      ...this.buildBaseEvent(),
      eventName: EVENT_NAMES.WIDGET_DISPLAY,
      eventOffsetMs: this.getEventOffsetMs(),
    });
  }

  async trackWidgetView(): Promise<void> {
    if (!this.shouldFireEvent(EVENT_NAMES.WIDGET_VIEW)) return;
    this.markEventFired(EVENT_NAMES.WIDGET_VIEW);

    const timeToInteractiveMs = this.widgetLoadTime
      ? Math.round(performance.now() - this.widgetLoadTime)
      : undefined;

    await this.sendEvent({
      ...this.buildBaseEvent(),
      eventName: EVENT_NAMES.WIDGET_VIEW,
      eventOffsetMs: this.getEventOffsetMs(),
      ...(timeToInteractiveMs && { timeToInteractiveMs }),
    });
  }

  async trackAdDisplay(
    ad: AdProperties,
    payloadAttributes?: Record<string, unknown>,
  ): Promise<void> {
    if (!this.shouldFireEvent(EVENT_NAMES.AD_DISPLAY, ad.offerId)) return;
    this.markEventFired(EVENT_NAMES.AD_DISPLAY, ad.offerId);

    if (this.shouldFireEvent(EVENT_NAMES.WIDGET_DISPLAY)) {
      await this.trackWidgetDisplay();
    }

    await this.sendEvent({
      ...this.buildBaseEvent(),
      eventName: EVENT_NAMES.AD_DISPLAY,
      eventOffsetMs: this.getEventOffsetMs(),
      ...this.buildAdEventFields(ad),
      ...this.buildPayloadAttributes(payloadAttributes),
    });
  }

  async trackAdView(
    ad: AdProperties,
    payloadAttributes?: Record<string, unknown>,
  ): Promise<void> {
    if (!this.shouldFireEvent(EVENT_NAMES.AD_VIEW, ad.offerId)) return;
    this.markEventFired(EVENT_NAMES.AD_VIEW, ad.offerId);

    if (this.shouldFireEvent(EVENT_NAMES.WIDGET_VIEW)) {
      await this.trackWidgetView();
    }

    await this.sendEvent({
      ...this.buildBaseEvent(),
      eventName: EVENT_NAMES.AD_VIEW,
      eventOffsetMs: this.getEventOffsetMs(),
      ...this.buildAdEventFields(ad),
      ...this.buildPayloadAttributes(payloadAttributes),
    });
  }

  async trackAdClick(
    ad: AdProperties,
    initiatingTarget: 'INLINE_ACTION_BUTTON' | 'INLINE_ASSET_TILE',
  ): Promise<void> {
    await this.sendEvent({
      ...this.buildBaseEvent(),
      eventName: EVENT_NAMES.AD_CLICK,
      eventOffsetMs: this.getEventOffsetMs(),
      ...this.buildAdEventFields(ad),
      vendorInitiatingTarget: initiatingTarget,
    });
  }

  async trackOfferTabBlocked(
    ad: AdProperties,
    initiatingTarget: 'INLINE_ACTION_BUTTON' | 'INLINE_ASSET_TILE',
    blockDiagnostics?: Record<string, unknown>,
  ): Promise<void> {
    await this.sendEvent(
      {
        ...this.buildBaseEvent(),
        eventName: EVENT_NAMES.OFFER_TAB_BLOCKED,
        eventOffsetMs: this.getEventOffsetMs(),
        ...this.buildAdEventFields(ad),
        vendorInitiatingTarget: initiatingTarget,
      },
      blockDiagnostics ? { blockDiagnostics } : undefined,
    );
  }

  async trackElementClick(
    initiatingTarget: VendorInitiatingTarget,
    ad?: AdProperties,
    payloadAttributes?: Record<string, unknown>,
  ): Promise<void> {
    await this.sendEvent({
      ...this.buildBaseEvent(),
      eventName: EVENT_NAMES.ELEMENT_CLICK,
      eventOffsetMs: this.getEventOffsetMs(),
      vendorInitiatingTarget: initiatingTarget,
      ...(ad && this.buildAdEventFields(ad)),
      ...this.buildPayloadAttributes(payloadAttributes),
    });
  }
}
